import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { EmailText, resendLabelKey, type ResendState } from '@features/EmailVerify';
import { paths } from '@shared/config';
import { cn } from '@shared/lib';
import { Button, Field, Input } from '@shared/ui';
import { useRequestResetMutation } from '../model/queries';
import { useResetEmailStore } from '../model/useResetEmailStore';

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
const RESEND_SECONDS = 45;
/** «✓ Отправлено» на кнопке после повторной отправки — как у письма подтверждения (useResendCooldown). */
const SENT_FLASH_MS = 2400;

export function ForgotPasswordContent() {
  const { t } = useTranslation();
  const setStoredEmail = useResetEmailStore((s) => s.setEmail);
  const requestReset = useRequestResetMutation();

  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resendLeft, setResendLeft] = useState(RESEND_SECONDS);
  const [resentFlash, setResentFlash] = useState(false);
  const [resendFailed, setResendFailed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Синхронная защита от второго клика: isPending обновится только следующим рендером, и двойной
  // клик в одном кадре отправлял два письма сброса (B8, 2026-10-20).
  const inFlightRef = useRef(false);

  const isPending = requestReset.isPending;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (sentTo === null || resendLeft <= 0) return;
    const id = window.setTimeout(() => setResendLeft((v) => v - 1), 1000);
    return () => window.clearTimeout(id);
  }, [resendLeft, sentTo]);

  useEffect(() => {
    if (!resentFlash) return;
    const id = window.setTimeout(() => setResentFlash(false), SENT_FLASH_MS);
    return () => window.clearTimeout(id);
  }, [resentFlash]);

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (inFlightRef.current || isPending) return;
    const value = email.trim();
    if (!isValidEmail(value)) {
      setError(t('auth.forgot.errors.invalidEmail'));
      inputRef.current?.focus();
      return;
    }
    setError(null);
    inFlightRef.current = true;
    requestReset.mutate(
      { email: value },
      {
        onSuccess: () => {
          setSentTo(value);
          setStoredEmail(value);
          setResendLeft(RESEND_SECONDS);
        },
        onSettled: () => {
          inFlightRef.current = false;
        },
      },
    );
  };

  const resend = () => {
    if (inFlightRef.current || resendLeft > 0 || isPending || !sentTo) return;
    inFlightRef.current = true;
    setResendFailed(false);
    requestReset.mutate(
      { email: sentTo },
      {
        onSuccess: () => {
          setResendLeft(RESEND_SECONDS);
          setResentFlash(true);
        },
        onError: () => setResendFailed(true),
        onSettled: () => {
          inFlightRef.current = false;
        },
      },
    );
  };

  // Подписи кнопки — те же, что у письма подтверждения (emailVerify.full): «Отправляем…», «✓ Отправлено»,
  // «Повтор через N с»; отсчёт — внутри кнопки, без отдельной пилюли (R2-04).
  const resendState: ResendState = resendFailed
    ? 'error'
    : isPending
      ? 'pending'
      : resentFlash
        ? 'sent'
        : resendLeft > 0
          ? 'cooldown'
          : 'idle';
  const resendLocked = resendState === 'pending' || resendState === 'sent' || resendState === 'cooldown';

  return (
    <AnimatePresence mode="wait" initial={false}>
      {sentTo === null ? (
        <motion.form
          key="form"
          onSubmit={submit}
          noValidate
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.28, ease: 'easeOut' }}
          className="flex w-full max-w-[380px] flex-col gap-3.5"
        >
          <h1 className="font-serif text-[30px] font-medium leading-tight tracking-[-0.02em] sm:text-[40px]">
            {t('auth.forgot.title')}
          </h1>
          <div className="max-w-[340px] text-[14px] leading-[1.55] text-muted">
            {t('auth.forgot.leadHead')} <b className="font-medium text-fg">{t('auth.forgot.leadStrong')}</b>
            {t('auth.forgot.leadTail')}
          </div>

          <Field label={t('auth.forgot.emailLabel')} error={error}>
            <Input
              ref={inputRef}
              type="email"
              className="ym-disable-keys"
              placeholder="you@example.com"
              autoComplete="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError(null);
              }}
              invalid={!!error}
              disabled={isPending}
            />
          </Field>

          <Button type="submit" size="lg" disabled={isPending} className="mt-1">
            <span>{isPending ? '…' : t('auth.forgot.submit')}</span>
            <span aria-hidden className="text-sm">→</span>
          </Button>

          <div className="mt-1 text-center text-[14px] text-muted">
            {t('auth.forgot.rememberPrefix')}{' '}
            <Link to={paths.auth} className="font-medium text-accent hover:underline">
              {t('auth.forgot.rememberLink')}
            </Link>
          </div>
        </motion.form>
      ) : (
        <motion.div
          key="sent"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.32, ease: 'easeOut' }}
          className="flex w-full max-w-[380px] flex-col gap-[18px]"
        >
          <motion.div
            initial={{ scale: 0.5, opacity: 0, rotate: -8 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            transition={{ duration: 0.55, type: 'spring', stiffness: 240, damping: 16 }}
            className="inline-flex h-14 w-14 items-center justify-center rounded-[18px] bg-accent/15 text-2xl text-accent"
          >
            ✉
          </motion.div>

          <h1 className="text-balance font-serif text-[28px] font-medium leading-[1.1] tracking-[-0.02em] sm:text-[36px]">
            {t('auth.forgot.sent.title')}{' '}
            <em className="font-normal italic text-accent">{t('auth.forgot.sent.titleEm')}</em>
          </h1>

          {/* Адрес — ПДн (amp-mask / ym-hide-content); переносится только после «@» (EmailText, R2-04). */}
          <div className="max-w-[360px] text-[14px] leading-[1.6] text-muted">
            {t('auth.forgot.sent.subPrefix')}{' '}
            <b className="amp-mask ym-hide-content font-mono text-[14px] font-medium text-fg [overflow-wrap:anywhere]">
              <EmailText email={sentTo} />
            </b>
            .
          </div>

          <div className="mt-2">
            <Link
              to={paths.auth}
              className="inline-flex w-full items-center justify-center gap-2 rounded-pill border border-line bg-transparent px-7 py-4 text-[15px] font-medium tracking-[0.02em] text-fg transition-colors hover:bg-elev"
            >
              {t('auth.forgot.sent.back')}
            </Link>
          </div>

          {/* До 390 px «Не пришло?» — своей строкой над кнопкой, шире — в ряд; подпись кнопки в одну строку (R2-04). */}
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-xs text-muted">
            <span className="max-[389.98px]:basis-full">{t('auth.forgot.sent.resendPrompt')}</span>
            <button
              type="button"
              onClick={resend}
              // Не disabled: нажатая кнопка держит фокус клавиатуры; повтор заблокирован в resend().
              aria-disabled={resendLocked || undefined}
              className={cn(
                // Область нажатия — 44 px по высоте (::before), ряд от этого не растёт.
                "relative whitespace-nowrap text-[14px] font-medium underline-offset-[3px] before:absolute before:inset-x-0 before:top-1/2 before:h-11 before:-translate-y-1/2 before:content-['']",
                resendLocked
                  ? 'cursor-not-allowed text-muted no-underline'
                  : 'text-accent underline decoration-accent/40 hover:decoration-accent',
              )}
            >
              {t(`emailVerify.full.${resendLabelKey[resendState]}`, { n: resendLeft })}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
